import {
    Box,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion'; 

import VehicleExploreSection from './VehicleExploreSection';
import DealerListSection from './DealerListSection';
import axiosInstance from '../services/axiosInstance';
import HeroSection from './HeroSection';
import Header from './Header';

// ✅ Import encoder
import { encodeId } from '../utils/idEncoder';

// --- 1. DEFINE THE ANIMATION WRAPPER ---
const ScrollReveal = ({ children, delay = 0 }: { children: React.ReactNode, delay?: number }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
                duration: 0.8,
                delay: delay,
                type: "spring",
                bounce: 0.3
            }}
            style={{ width: '100%' }}
        >
            {children}
        </motion.div>
    );
};

const HomeShowcase = () => {
    const navigate = useNavigate();
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [dealerData, setDealerData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [lightCommercialList, setLightCommercialList] = useState<any[]>([]);
    const [heavyCommercialList, setHeavyCommercialList] = useState<any[]>([]);
    const [adSpaceList, setAdSpaceList] = useState<any[]>([]);
    const [adSpaceBanner, setAdSpaceBanner] = useState<any[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.post(
                    "/CustomerVehicle/VehicleMultipleDataList",
                    {
                        vehicleBodyTypeData: true,
                        vehicleTransmissionTypeData: true,
                        vehicleClassData: true,
                        vehicleBrandData: true,
                        vehicleModelData: true,
                        dealerCompanyData: true,
                        adSpaceData: true,
                    },
                    { headers: { "Content-Type": "application/json" } }
                );

                const result = response.data as any;

                if (result.responseCode === "00" && result.responseData) {
                    setDealerData(result.responseData.dealerCompanyData || []);
                    setAdSpaceList(result.responseData.adSpaceData || []);
                    const homePageBanner = (result.responseData.adSpaceData || []).filter(
                        (ad: any) => ad.adPositionCode === "homePage-Banner"
                    );
                    setAdSpaceBanner(homePageBanner);
                } else {
                    console.error(result.responseMessage || "Failed to fetch vehicle metadata");
                }
            } catch {
                console.error("Error fetching vehicle metadata");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

useEffect(() => {
        const fetchVehicles = async () => {
            setLoading(true);
            try {
                // ✅ Pass pagination parameters to the backend
                const payload = {
                    pageNumber: 1,
                    pageSize: 5
                };

                const response = await axiosInstance.post(
                    "/CustomerVehicle/VehicleGetListPublished",
                    payload, // ✅ Send the payload here instead of {}
                    { headers: { "Content-Type": "application/json" } }
                );

                const result = response.data as any;

                if (result.responseCode === "00" && result.responseData) {
                    // The backend will now safely return a maximum of 20 items
                    const vehicleList = result.responseData.vehicleList || result.responseData.VehicleList || [];
                    
                    // Class ID 2 = Light, Class ID 3 = Heavy
                    const lightCommercial = vehicleList.filter((v: any) => v.vehicleClassId === 2);
                    const heavyCommercial = vehicleList.filter((v: any) => v.vehicleClassId === 3);

                    setVehicles(vehicleList);
                    setLightCommercialList(lightCommercial);
                    setHeavyCommercialList(heavyCommercial);
                } else {
                    console.error("Failed to load vehicles:", result.responseMessage);
                }
            } catch {
                console.error("Error fetching vehicles");
            } finally {
                setLoading(false);
            }
        };

        fetchVehicles();
    }, []);

    return (
        <>
            <Header />
            
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 1200,
                    mx: 'auto',
                    position: 'relative',
                    mt: { xs: 8, md: 12 },
                }}
            >
                {/* --- Hero Section Animation --- */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                >
                    <HeroSection />
                </motion.div>
            </Box>

            {/* Filters & Listings */}
            <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', pb: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                
                {vehicles?.length > 0 && (
                    <ScrollReveal>
                        <VehicleExploreSection
                            title="Explore Our Vehicles"
                            navigateTo="/vehicle-listing" // No ID here, generic list
                            vehicle={vehicles}
                        />
                    </ScrollReveal>
                )}

                {dealerData?.length > 0 && (
                    <ScrollReveal delay={0.1}>
                        <DealerListSection
                            dealerData={dealerData}
                            adSpaceList={adSpaceList}
                        />
                    </ScrollReveal>
                )}

                {lightCommercialList?.length > 0 && (
                    <ScrollReveal delay={0.1}>
                        <VehicleExploreSection
                            title="Explore Light Vehicles Commercial"
                            // ✅ ENCODE: Class ID 2
                            navigateTo={`/vehicle-listing?vehicleClassIds=${encodeId(2)}`}
                            vehicle={lightCommercialList}
                        />
                    </ScrollReveal>
                )}

                {heavyCommercialList?.length > 0 && (
                    <ScrollReveal delay={0.1}>
                        <VehicleExploreSection
                            title="Explore Heavy Vehicles Commercial"
                            // ✅ ENCODE: Class ID 3
                            navigateTo={`/vehicle-listing?vehicleClassIds=${encodeId(3)}`}
                            vehicle={heavyCommercialList}
                        />
                    </ScrollReveal>
                )}

            </Box>
        </>
    );
};

export default HomeShowcase;